<template>
  <div class="categories-section">
    <div class="categories-section-title">{{ t('assist.categories') }}:</div>
    <div class="categories">
      <BbBtn
        v-for="category in categories"
        :key="category.id"
        :class="['category-button', { active: isActiveCategory(category.id) }]"
        size="large"
        type="quaternary"
        @click="setActiveCategory(category)"
      >
        <BbIcon v-if="isActiveCategory(category.id)" size="small" name="checkmark-small" />
        <span class="category-name">{{ category.title }}</span>
      </BbBtn>
    </div>

    <div class="divider"></div>

    <div class="search-container">
      <BbInput v-model="filterValue" class="search-input" size="small" :placeholder="t('common.search')">
        <template #prepend-inner>
          <BbIcon size="small" name="search" />
        </template>
      </BbInput>
    </div>
  </div>
</template>

<script>
import { ref, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
  name: 'CategoryNavigation',
  props: {
    categories: { type: Array, required: true, default: () => [] },
    activeCategories: { type: Array, required: true, default: () => [] },
  },
  emits: ['update:active-category', 'update:search-query'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const filterValue = ref('');

    // Debounced search
    let debounceTimer = null;

    watch(filterValue, (newValue) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        emit('update:search-query', newValue);
      }, 300);
    });

    onUnmounted(() => {
      if (debounceTimer) clearTimeout(debounceTimer);
    });

    // Emit full category object
    const setActiveCategory = (category) => {
      emit('update:active-category', category);
    };

    const isActiveCategory = (categoryId) => props.activeCategories.some((cat) => cat.id === categoryId);

    return {
      t,
      filterValue,
      setActiveCategory,
      isActiveCategory,
    };
  },
};
</script>

<style scoped lang="scss">
.categories-section {
  padding: $bb-spacing-4;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $bb-spacing-2;

  .categories-section-title {
    @include bb-medium-text-styles('sm');
  }

  .divider {
    width: 1px;
    height: $bb-spacing-6;
    background-color: $bb-neutral-4;
  }
}

.categories {
  display: flex;
  gap: $bb-spacing-2;
  flex-wrap: wrap;
}

.category-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $bb-spacing-2;
  transition: all 0.2s ease;
  padding: $bb-spacing-4 !important;

  &.active {
    background-color: $bb-neutral-5;

    .category-name {
      color: $bb-neutral-1;
    }
  }

  .category-name {
    @include bb-medium-text-styles('sm');
    color: $bb-neutral-3;
  }
}

.search-container {
  flex: 1;
}

.search-input {
  width: 100%;
  @include bb-medium-text-styles('sm');

  :deep(.v-input) {
    .v-input__control {
      .v-field {
        .v-field__outline__start,
        .v-field__outline__end {
          border-color: transparent !important;
        }
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .categories-section {
    padding: $bb-spacing-3 $bb-spacing-4;
    flex-direction: column;
    align-items: stretch;
    gap: $bb-spacing-3;
  }

  .categories {
    justify-content: center;
  }

  .search-container {
    max-width: none;
    min-width: auto;
  }
}
</style>
